import { useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalPlayer } from '@/context/GlobalPlayerContext';
import { CATEGORIES, JOURNEY_STAGES } from '@/constants/playlists';
import { getDailyVerseSongCategory, isDailyVerseSong } from '@/constants/dailyVerses';
import { useSongs } from '@/hooks/useSongs';
import { useAuth } from '@/hooks/useAuth';
import { usePlayerState } from '@/hooks/usePlayerState';
import { canAccessJourneySong } from '@/lib/journeyValidation';
import { supabase } from '@/lib/supabase';
import { usePremiumUpgrade } from '@/context/PremiumUpgradeContext';
import { Song } from '@/types/player';

export const useGlobalPlayerControls = () => {
  const {
    loadPlaylist,
    setCurrentIndex,
    setCurrentPlaylistName,
    loadSong,
    currentSong,
    setCurrentSong,
  } = useGlobalPlayer();
  const { updatePlayerState } = usePlayerState();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoadingRef = useRef(false);
  const { user } = useAuth();
  const { data: songs, getSongById } = useSongs();
  const { showUpgradeModal } = usePremiumUpgrade();
  
  const isCategoryLoadingRef = useRef(false);
  
  // Função para verificar se deve mostrar o modal de upgrade
  const shouldShowUpgradeModal = useCallback(() => {
    if (!currentSong || user?.tipo_de_conta === 'premium') return false;
    return Number(currentSong.id) === 453;
  }, [currentSong, user?.tipo_de_conta]);

  const playCategorySong = async (songId: number, category: string, playlist?: number[]) => {
    if (isCategoryLoadingRef.current) {
      return;
    }

    try {
      isCategoryLoadingRef.current = true;
      updatePlayerState('navigating', 'navigate-to-player');

      if (!playlist) {
        playlist = CATEGORIES[category]?.songIds || [];
      }

      let targetIndex = playlist.findIndex(id => Number(id) === Number(songId));
      
      if (targetIndex === -1) {
        const playlistCopy = [...playlist];
        playlistCopy.unshift(Number(songId));
        targetIndex = 0;
        
        await Promise.race([
          loadPlaylist(playlistCopy, 'category', category, undefined, targetIndex, 'user-click'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout ao carregar playlist')), 8000))
        ]);
      } else {
        await Promise.race([
          loadPlaylist(playlist, 'category', category, undefined, targetIndex, 'user-click'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout ao carregar playlist')), 8000))
        ]);
      }

      if (location.pathname !== '/player') {
        navigate('/player');
        setTimeout(() => {
          updatePlayerState('idle', 'navigation-complete');
        }, 200);
      }

    } catch (error) {
      throw error;
    } finally {
      setTimeout(() => {
      isCategoryLoadingRef.current = false;
      updatePlayerState('idle', 'navigation-complete');
      }, 500);
    }
  };

  const playJourneySong = async (songId: number, stageId?: number | string) => {
    if (!songId) {
      return;
    }

    // Se o usuário é gratuito e está na música 453, mostra o modal de upgrade
    if (shouldShowUpgradeModal()) {
      showUpgradeModal();
      return;
    }

    // VALIDAÇÃO CRÍTICA: apenas IDs 22, 23, 453 são gratuitos na jornada
    if (!canAccessJourneySong(Number(songId), user?.tipo_de_conta)) {
      const { showToast } = await import('@/hooks/useToast');
      showToast('Esta música está disponível apenas para usuários premium', 'error');
      return;
    }

    try {
      updatePlayerState('navigating', 'navigate-to-player');

      const fullJourneyPlaylist = JOURNEY_STAGES.flatMap(stage => stage.songIds);

      const targetIndex = fullJourneyPlaylist.findIndex(id => Number(id) === Number(songId));

      if (targetIndex === -1) {
        await loadSong(songId, 'journey');
        if (location.pathname !== '/player') {
          navigate('/player');
        }
        return;
      }

      const targetStage = JOURNEY_STAGES.find(stage => stage.songIds.includes(Number(songId)));
      const targetStageId = targetStage?.id;

      await loadPlaylist(fullJourneyPlaylist, 'journey', undefined, targetStageId, targetIndex);

      if (location.pathname !== '/player') {
        navigate('/player');
      }

    } catch (error) {
      try {
        await loadSong(songId, 'journey');
        if (location.pathname !== '/player') {
          navigate('/player');
        }
      } catch (fallbackError) {
      }
    } finally {
      setTimeout(() => updatePlayerState('idle', 'navigation-complete'), 200);
    }
  };

  const playFavoriteSong = async (songId: number) => {
    try {
      if (!songId || isNaN(Number(songId))) {
        return;
      }

      if (isLoadingRef.current) {
        return;
      }
      
      isLoadingRef.current = true;

      try {
        const loadFavoritesPromise = async () => {
          if (!user?.id) {
            throw new Error('Usuário não logado');
          }

          // Etapa 1: Buscar IDs
          const { data: favoriteRelations, error: favoritesError } = await supabase
            .from('favoritos')
            .select('song_id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (favoritesError) throw favoritesError;

          const favoriteIds = (favoriteRelations || []).map(item => item.song_id);

          if (favoriteIds.length === 0) {
            throw new Error('Nenhuma música favorita encontrada');
          }

          const targetIndex = favoriteIds.findIndex(id => id === Number(songId));
          if (targetIndex === -1) {
            throw new Error('Música não encontrada nos favoritos');
          }
          
          // Etapa 2: Validar a música alvo (se necessário)
          const { data: targetSong, error: songError } = await supabase
            .from('songs')
            .select('is_free')
            .eq('id', songId)
            .single();

          if (songError) throw songError;

          if (targetSong && targetSong.is_free === false && user?.tipo_de_conta !== 'premium') {
            const { showToast } = await import('@/hooks/useToast');
            showToast('Esta música está disponível apenas para usuários premium', 'error');
            return;
          }
          
          await loadPlaylist(favoriteIds, 'favorites', undefined, undefined, targetIndex);
          
          // Garantir que a seção e nome da playlist sejam definidos corretamente
          setCurrentPlaylistName('Favoritos');
          
          if (location.pathname !== '/player') {
            updatePlayerState('navigating', 'navigate-to-player');
            navigate('/player');
            setTimeout(() => updatePlayerState('idle', 'navigation-complete'), 200);
          }
        };

        await loadFavoritesPromise();
      } finally {
        isLoadingRef.current = false;
      }
    } catch (error) {
      const { showToast } = await import('@/hooks/useToast');
      showToast('Erro ao carregar música. Tente novamente.', 'error');
      isLoadingRef.current = false;
    }
  };

  const playDailyVerseSong = async (songId: number) => {
    if (!songId) {
      return;
    }

    // Otimização: Define a música atual a partir do cache para exibir a capa imediatamente.
    const cachedSong = getSongById(songId);
    if (cachedSong) {
      // Verificação premium antecipada
      if (user?.tipo_de_conta !== 'premium' && cachedSong.is_free === false) {
        const { showToast } = await import('@/hooks/useToast');
        showToast('Esta música está disponível apenas para usuários premium', 'error');
        return;
      }
      setCurrentSong(cachedSong);
    }

    try {
      updatePlayerState('navigating', 'navigate-to-player');

      // Obtém a categoria da música do versículo do dia
      const category = getDailyVerseSongCategory(songId);
      
      if (!category) {
        // Se não encontrar a categoria, carrega apenas a música individual
        const { data: song, error } = await supabase
          .from('songs')
          .select('*')
          .eq('id', songId)
          .single();

        if (error || !song) {
          throw new Error('Erro ao carregar música');
        }

        // Verificação premium após buscar do banco
        if (user?.tipo_de_conta !== 'premium' && song.is_free === false) {
          const { showToast } = await import('@/hooks/useToast');
          showToast('Esta música está disponível apenas para usuários premium', 'error');
          return;
        }

        await loadSong(songId, 'category');
      } else {
        // Se encontrar a categoria, carrega a playlist completa da categoria
        const playlist = CATEGORIES[category]?.songIds || [];
        const targetIndex = playlist.findIndex(id => Number(id) === Number(songId));

        if (targetIndex === -1) {
          // Se a música não estiver na playlist, adiciona no início
          const playlistCopy = [songId, ...playlist];
          await loadPlaylist(playlistCopy, 'category', category, undefined, 0, 'daily-verse');
        } else {
          // Se a música estiver na playlist, carrega normalmente
          await loadPlaylist(playlist, 'category', category, undefined, targetIndex, 'daily-verse');
        }
      }

      if (location.pathname !== '/player') {
        navigate('/player');
      }

    } catch (error) {
      const { showToast } = await import('@/hooks/useToast');
      showToast('Erro ao carregar música. Tente novamente.', 'error');
    } finally {
      updatePlayerState('idle', 'navigation-complete');
    }
  };

  return {
    playCategorySong,
    playJourneySong,
    playFavoriteSong,
    playDailyVerseSong
  };
}; 